import { isAxiosError } from "axios";

import { createAppAsyncThunk } from "../appAsyncThunk";
import type {
    AssignCompanyParams,
    CreateCompanyGroupParams,
    CurrentCompanyGroup,
    ICompanyGroup,
    ICompanyGroupMember,
    UpdateCompanyGroupParams,
} from ".";
import { companyGroupSelector } from "./companyGroupsStore";

// Captures the HTTP status so the slice can distinguish a 409 conflict.
const toRejection = (error: any) => ({ status: error?.response?.status, message: error?.response?.data?.message });

export const fetchCompanyGroups = createAppAsyncThunk<ICompanyGroup[], void>(
    "companyGroups/fetchAll",
    async (_, { extra, rejectWithValue }) => {
        try {
            return await extra.companyGroupRepository.fetchCompanyGroups();
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const createCompanyGroup = createAppAsyncThunk<{ id: number; name: string }, CreateCompanyGroupParams>(
    "companyGroups/create",
    async (params, { extra, rejectWithValue }) => {
        try {
            return await extra.companyGroupRepository.createCompanyGroup(params);
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const updateCompanyGroup = createAppAsyncThunk<ICompanyGroup, UpdateCompanyGroupParams>(
    "companyGroups/update",
    async (params, { extra, rejectWithValue }) => {
        try {
            return await extra.companyGroupRepository.updateCompanyGroup(params);
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const deleteCompanyGroup = createAppAsyncThunk<number, number>(
    "companyGroups/delete",
    async (id, { extra, rejectWithValue }) => {
        try {
            await extra.companyGroupRepository.deleteCompanyGroup(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const fetchGroupCompanies = createAppAsyncThunk<{ groupId: number; members: ICompanyGroupMember[] }, number>(
    "companyGroups/fetchCompanies",
    async (groupId, { extra, rejectWithValue }) => {
        try {
            const members = await extra.companyGroupRepository.fetchGroupCompanies(groupId);
            return { groupId, members };
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const assignCompanyToGroup = createAppAsyncThunk<AssignCompanyParams, AssignCompanyParams>(
    "companyGroups/assignCompany",
    async (params, { extra, rejectWithValue }) => {
        try {
            await extra.companyGroupRepository.assignCompany(params);
            return params;
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const unassignCompanyFromGroup = createAppAsyncThunk<AssignCompanyParams, AssignCompanyParams>(
    "companyGroups/unassignCompany",
    async (params, { extra, rejectWithValue }) => {
        try {
            await extra.companyGroupRepository.unassignCompany(params);
            return params;
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const fetchSharedFields = createAppAsyncThunk<{ groupId: number; fields: string[]; catalog: string[] }, number>(
    "companyGroups/fetchSharedFields",
    async (groupId, { extra, rejectWithValue }) => {
        try {
            const { fields, catalog } = await extra.companyGroupRepository.fetchSharedFields(groupId);
            return { groupId, fields, catalog };
        } catch (error: any) {
            return rejectWithValue(toRejection(error) as any);
        }
    },
);

export const updateSharedFields = createAppAsyncThunk<
    { groupId: number; fields: string[] },
    { groupId: number; fields: string[] }
>("companyGroups/updateSharedFields", async ({ groupId, fields }, { extra, rejectWithValue }) => {
    try {
        const result = await extra.companyGroupRepository.setSharedFields(groupId, fields);
        return { groupId, fields: result.fields };
    } catch (error: any) {
        return rejectWithValue(toRejection(error) as any);
    }
});

export const fetchCurrentCompanyGroup = createAppAsyncThunk<CurrentCompanyGroup | undefined>(
    "companyGroups/getCurrent",
    async (_, { getState, extra, rejectWithValue }) => {
        try {
            const { currentGroup } = companyGroupSelector(getState());
            if (currentGroup) return currentGroup;

            return await extra.companyGroupRepository.getCurrent();
        } catch (error: any) {
            if (isAxiosError(error) && error.response?.status === 404) {
                return undefined;
            }
            return rejectWithValue(toRejection(error) as any);
        }
    },
);
